import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Stethoscope, MessageSquare, PenLine } from "lucide-react";

const REASONS = [
  { Ic: MessageSquare, t: "A question", d: "About anything here, or about where to start." },
  { Ic: PenLine, t: "A correction", d: "If something we wrote is wrong, we want to know first." },
  { Ic: Stethoscope, t: "A specialist who wants in", d: "Real name and credentials to us, alias-only to everyone else." },
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { name: name || null, email: email || null, message });
      setSent(true);
      toast("Received. A person will read it, not a machine.");
    } catch (err) {
      toast("That did not go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-16" data-testid="contact-page">
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-36 left-[-8%] w-[560px] h-[560px] opacity-70"
          style={{ background: "radial-gradient(circle, rgba(252,238,228,0.95), transparent 66%)" }} />
        <div className="pointer-events-none absolute top-24 right-[-10%] w-[460px] h-[460px] opacity-60"
          style={{ background: "radial-gradient(circle, rgba(239,234,252,0.95), transparent 68%)" }} />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-24">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="max-w-xl">
            <p className="kicker text-[var(--ink-soft)]">Contact</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.04]">Say it plainly</h1>
            <p className="mt-5 text-[var(--ink-soft)]">
              Name and email are optional, like everything else here. Leave them out and it still gets read.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* ---- the form ---- */}
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}>
              {sent ? (
                <div className="rounded-[2rem] border border-[var(--line)] bg-white/60 p-9" data-testid="contact-sent">
                  <motion.span className="block w-12 h-12 rounded-full mb-5" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", damping: 14 }} style={{ background: "linear-gradient(140deg, #6F5AA8, #E8B84B)" }} />
                  <h2 className="font-display text-3xl">It arrived</h2>
                  <p className="mt-3 text-[var(--ink-soft)]">Thank you for writing. If you left an email, expect an answer from a human.</p>
                  <button onClick={() => { setSent(false); setMessage(""); }} className="mt-6 btn-line rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="contact-again-btn">
                    Write another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="rounded-[2rem] border border-[var(--line)] bg-white/55 p-7 sm:p-9 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="kicker text-[var(--ink-soft)]">Name, if you like</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Or leave it blank"
                        className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/70 px-4 py-2.5 outline-none focus:border-[var(--ink)] transition-colors" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="kicker text-[var(--ink-soft)]">Email, for an answer</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Also optional"
                        className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/70 px-4 py-2.5 outline-none focus:border-[var(--ink)] transition-colors" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className="kicker text-[var(--ink-soft)]">The message</label>
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} required minLength={3} rows={7} placeholder="What is on your mind"
                      className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/70 px-4 py-3 outline-none focus:border-[var(--ink)] transition-colors resize-none" data-testid="contact-message-input" />
                    <p className="mt-2 text-xs text-[var(--ink-soft)] font-mono-nc">{message.trim().length} characters · nothing is published anywhere</p>
                  </div>
                  <button type="submit" disabled={busy || !message.trim()} className="btn-ink rounded-full px-7 py-3 font-semibold disabled:opacity-50" data-testid="contact-submit-btn">
                    {busy ? "Sending" : "Send it"}
                  </button>
                </form>
              )}
            </motion.div>

            {/* ---- what tends to arrive ---- */}
            <motion.aside className="lg:col-span-5 lg:pt-4" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}>
              <p className="kicker text-[var(--ink-soft)]">What usually arrives</p>
              <div className="mt-4 space-y-3">
                {REASONS.map((r, i) => (
                  <motion.div key={r.t} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.24 + i * 0.08 }}
                    className={`flex gap-4 rounded-[1.5rem] border border-[var(--line)] bg-white/45 p-5 ${i === 1 ? "lg:ml-6" : ""}`}>
                    <span className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center" style={{ background: "rgba(111,90,168,0.12)", color: "#6F5AA8" }}>
                      <r.Ic size={16} />
                    </span>
                    <div>
                      <p className="font-medium">{r.t}</p>
                      <p className="text-sm text-[var(--ink-soft)] mt-1 leading-relaxed">{r.d}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-5 rounded-[1.5rem] p-6" style={{ background: "linear-gradient(140deg, #2A2338, #3A2E52)" }}>
                <p className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "rgba(255,255,255,0.4)" }}>One thing this is not</p>
                <p className="text-white mt-2.5 leading-relaxed text-sm">
                  This is not a way to reach a doctor about something happening now. If it is urgent, call emergency services on 112.
                </p>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>
    </main>
  );
}
